



import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// --- THEME COLORS ---
const BR = "#1D4ED8";
const DK = "#0F172A";

// --- CONSTANTS ---
const PROPERTY_TYPES = ["Residential Plots (NA)", "Farm House Plot", "Flat / Apartment", "Villa / Bungalow"];
const BUDGETS = ["Under 20 Lakhs", "20 Lakhs - 50 Lakhs", "50 Lakhs - 1 Crore", "1 Crore+"];
const SOURCES = ["whatsapp", "Facebook", "Instagram", "Visit", "Referral", "Other", "News"];
const GOALS = ["Building a personal home", "Investment", "Rental income"];
const TIMELINES = ["Within 30 days", "1-3 months", "Not Sure"];
const USERS = ["Admin", "Sales Manager", "Rahul Patil", "Sneha Kulkarni", "Amit Shinde"];
const COMMUNICATION_MODES = ["Call", "WhatsApp", "Visit", "Email", "Meeting"];
const LEAD_FROM = ["Self (Customer)", "Admin", "Telecaller", "Sales Executive", "Marketing Agency"];
const LEAD_STATUS = ["Follow", "Close"];
const LOCATIONS = ["Pune City", "Hinjewadi", "Wakad", "Baner", "Hadapsar", "Mumbai-Pune Highway", "Chakan", "Talegaon", "Lonavala", "PCMC"];

const labelStyleCommon = { 
  fontSize: "14px", 
  fontWeight: "700", 
  color: BR, 
  marginBottom: "8px", 
  display: "block" 
};

// --- MULTI-SELECT DROPDOWN COMPONENT ---
const MultiSelectDropdown = ({ options, value = [], onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (opt) => {
    const newValue = value.includes(opt)
      ? value.filter((item) => item !== opt)
      : [...value, opt];
    onChange(newValue);
  };

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          border: isOpen ? `2px solid ${BR}` : "1.5px solid #CBD5E1",
          borderRadius: "8px",
          padding: "6px 12px",
          minHeight: "40px",
          backgroundColor: "#fff",
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          cursor: "pointer",
          alignItems: "center",
          boxSizing: "border-box"
        }}
      >
        {value.length > 0 ? (
          value.map(val => (
            <span key={val} style={{ background: BR, color: "white", padding: "2px 8px", borderRadius: "4px", fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
              {val} 
              <span onClick={(e) => { e.stopPropagation(); toggleOption(val); }} style={{ cursor: "pointer", fontWeight: "bold" }}>×</span>
            </span>
          ))
        ) : (
          <span style={{ color: "#94a3b8", fontSize: "14px" }}>-- Select {placeholder} --</span>
        )}
        <span style={{ marginLeft: "auto", fontSize: "10px", color: "#64748b" }}>{isOpen ? "▲" : "▼"}</span>
      </div>

      {isOpen && (
        <div style={{ position: "absolute", top: "45px", left: 0, right: 0, background: "white", border: "1px solid #CBD5E1", borderRadius: "8px", zIndex: 1000, boxShadow: "0 10px 15px rgba(0,0,0,0.1)", maxHeight: "200px", overflowY: "auto" }}>
          <input 
            type="text" 
            placeholder="Search location..." 
            style={{ width: "100%", padding: "10px", border: "none", borderBottom: "1px solid #eee", outline: "none", boxSizing: "border-box" }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {filteredOptions.length > 0 ? filteredOptions.map(opt => (
            <div 
              key={opt} 
              onClick={() => toggleOption(opt)}
              style={{ padding: "10px 12px", fontSize: "13px", cursor: "pointer", display: "flex", justifyContent: "space-between", background: value.includes(opt) ? "#eff6ff" : "white", borderBottom: "1px solid #f1f5f9" }}
            >
              {opt} {value.includes(opt) && <span style={{ color: BR, fontWeight: "bold" }}>✓</span>}
            </div>
          )) : (
            <div style={{ padding: "10px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

// --- SEARCHABLE DROPDOWN COMPONENT (Single Select) ---
const SearchableDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const getSelectedLabel = () => {
    const found = options.find(opt => (opt.id || opt) === value);
    return found ? (found.name || found) : "";
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(""); 
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => {
    const label = (opt.name || opt).toString().toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={isOpen ? searchTerm : getSelectedLabel()}
          placeholder={!isOpen ? `-- Select ${placeholder} --` : "Type to search..."}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            border: isOpen ? `2px solid ${BR}` : "1.5px solid #CBD5E1",
            borderRadius: "8px",
            padding: "10px 35px 10px 12px",
            fontSize: "14px",
            color: "#334155",
            width: "100%",
            height: "40px",
            boxSizing: "border-box",
            outline: "none",
            backgroundColor: "#fff",
            transition: "all 0.2s"
          }}
        />
        <span style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "#64748b", pointerEvents: "none" }}>
          {isOpen ? "▲" : "▼"}
        </span>
      </div>

      {isOpen && (
        <div style={{ position: "absolute", top: "42px", left: 0, right: 0, backgroundColor: "#fff", border: "1px solid #CBD5E1", borderRadius: "8px", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", zIndex: 9999, maxHeight: "150px", overflowY: "auto" }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => {
              const optId = opt.id || opt;
              const optName = opt.name || opt;
              return (
                <div
                  key={index}
                  onClick={() => {
                    onChange(optId);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  style={{ padding: "8px 12px", fontSize: "13px", cursor: "pointer", backgroundColor: value === optId ? "#eff6ff" : "transparent", borderBottom: "1px solid #f1f5f9" }}
                >
                  {optName}
                </div>
              );
            })
          ) : (
            <div style={{ padding: "10px", fontSize: "13px", color: "#64748b", textAlign: "center" }}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

// --- FORM INPUT COMPONENT ---
const FormInput = ({ type = "text", placeholder, options, value, onChange, isSelect = false }) => {
  const [isFocused, setIsFocused] = useState(false);

  if (isSelect) {
    return <SearchableDropdown options={options} value={value} onChange={onChange} placeholder={placeholder} />;
  }

  return (
    <input
      type={type}
      style={{
        border: isFocused ? `2px solid ${BR}` : "1.5px solid #CBD5E1",
        borderRadius: "8px",
        padding: "10px 12px",
        fontSize: "14px",
        color: "#334155",
        width: "100%",
        boxSizing: "border-box",
        height: "40px",
        outline: "none",
        boxShadow: isFocused ? "0 0 0 3px rgba(29, 78, 216, 0.15)" : "none",
        transition: "all 0.2s ease"
      }}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    />
  );
};

// --- MODAL COMPONENT ---
function AddCustomerModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", gender: "", dob: "", 
    designation: "", pincode: "", address: "", city: "", state: ""
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "auto");
  }, []);

  const handleSave = () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      alert("Name and Mobile are required!");
      return;
    }
    onSave({ ...form, id: Date.now() });
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(231, 236, 247, 0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "10px" }}>
      <div style={{ background: "white", width: "100%", maxWidth: "850px", borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", maxHeight: "95vh" }}>
        <div style={{ background: DK, padding: "15px 20px", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 700, fontSize: "16px" }}>NEW CUSTOMER REGISTRATION</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: "22px" }}>✕</button>
        </div>
        <div style={{ padding: "20px", overflowY: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "15px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyleCommon}>Full Name *</label>
              <FormInput placeholder="Enter Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
              <div><label style={labelStyleCommon}>Mobile No *</label><FormInput placeholder="10-digit number" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} /></div>
              <div><label style={labelStyleCommon}>Email Address</label><FormInput placeholder="example@mail.com" value={form.email} onChange={(v) => setForm({ ...form, email: v })} /></div>
              <div><label style={labelStyleCommon}>Gender</label><FormInput isSelect options={["Male", "Female", "Other"]} value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} placeholder="Gender" /></div>
              <div><label style={labelStyleCommon}>Birth Date</label><FormInput type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} /></div>
              <div><label style={labelStyleCommon}>Designation</label><FormInput placeholder="e.g. Developer" value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} /></div>
              <div><label style={labelStyleCommon}>Pincode</label><FormInput placeholder="6-digit PIN" value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} /></div>
              <div><label style={labelStyleCommon}>City</label><FormInput placeholder="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} /></div>
              <div><label style={labelStyleCommon}>State</label><FormInput placeholder="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} /></div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyleCommon}>Full Address</label>
              <FormInput placeholder="House No, Street, Area..." value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            </div>
          </div>
        </div>
        <div style={{ padding: "15px 20px", borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end", gap: "12px", background: "#F8FAFC" }}>
          <button onClick={onClose} style={{ padding: "8px 20px", borderRadius: "50px", border: "1px solid #CBD5E1", background: "white", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: "8px 25px", borderRadius: "50px", border: "none", background: BR, color: "white", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Save Customer</button>
        </div>
      </div>
    </div>
  );
}

// --- MAIN PAGE ---
const LeadEnquiry = () => {
  const navigate = useNavigate();
  
  // आजची तारीख मिळवण्यासाठी हेल्पर
  const today = new Date().toISOString().split('T')[0];

  const [customers, setCustomers] = useState([{ id: 1, name: "Anita", mobile: "9876543210", city: "Pune", designation: "Developer", email: "anita@test.com" }]);
  const [selectedCustId, setSelectedCustId] = useState("");
  const [showModal, setShowModal] = useState(false);
  
  const [formState, setFormState] = useState({
    leadDate: today,          // Default current date
    followUpDate: today,      // Default current date
    assignTo: "",
    source: "", 
    propertyType: "", 
    budget: "", 
    goal: "", 
    timeline: "",
    remarks: [], 
    nextCommunication: "", 
    followUpPerson: "", 
    leadFrom: "", 
    leadStatus: ""
  });

  const selectedCustomer = customers.find(c => String(c.id) === String(selectedCustId));

  const handleCustomerSave = (newCust) => {
    setCustomers(prev => [...prev, newCust]);
    setSelectedCustId(newCust.id);
    setShowModal(false);
  };

  return (
    <div style={{ width: "100%", padding: "10px", boxSizing: "border-box" }}>
      <div style={{ 
        background: "white", 
        borderRadius: "15px", 
        padding: "40px 20px 120px 20px", 
        boxShadow: "0 10px 25px rgba(0,0,0,0.05)", 
        position: "relative", 
        border: "1px solid #E2E8F0", 
        maxWidth: "1200px", 
        margin: "20px auto", 
        overflow: "visible" 
      }}>
        
        <div style={{ position: "absolute", top: "-18px", left: "50%", transform: "translateX(-50%)", background: BR, color: "white", padding: "10px 30px", borderRadius: "10px", fontWeight: "800", fontSize: "13px", letterSpacing: "1px", whiteSpace: "nowrap", textAlign: "center", zIndex: 10 }}>
          CREATE NEW LEAD / ENQUIRY
        </div>

        {/* Row 1 */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "15px", alignItems: "flex-end", marginBottom: "20px" }}>
          <div style={{ gridColumn: "span 2", display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyleCommon}>Select customer</label>
              <FormInput isSelect options={customers} value={selectedCustId} onChange={(v) => setSelectedCustId(v)} placeholder="Customer" />
            </div>
            <button onClick={() => setShowModal(true)} style={{ background: BR, color: "white", border: "none", width: "40px", height: "40px", borderRadius: "8px", cursor: "pointer", fontSize: "20px", fontWeight: "bold", flexShrink: 0 }}>+</button>
          </div>
          
          {/* Lead Date Input */}
          <div>
            <label style={labelStyleCommon}>Lead date</label>
            <FormInput 
              type="date" 
              value={formState.leadDate} 
              onChange={(v) => setFormState({...formState, leadDate: v})} 
            />
          </div>

          <div><label style={labelStyleCommon}>Assign To</label><FormInput isSelect options={USERS} value={formState.assignTo} onChange={(v) => setFormState({...formState, assignTo: v})} placeholder="Assignee" /></div>
          
          {/* Follow-up Date Input */}
          <div>
            <label style={labelStyleCommon}>Follow-up date</label>
            <FormInput 
              type="date" 
              value={formState.followUpDate} 
              onChange={(v) => setFormState({...formState, followUpDate: v})} 
            />
          </div>
        </div>

        {/* Selected Customer Info Bar */}
        <div style={{ minHeight: "65px", width: "100%", marginBottom: "20px" }}>
          {selectedCustomer && (
            <div style={{ background: "#F1F7FF", padding: "15px", borderRadius: "10px", borderLeft: `6px solid ${BR}`, display: "flex", flexWrap: "wrap", gap: "15px 30px", fontSize: "14px", color: "#475569" }}>
              <div><strong>Mobile:</strong> {selectedCustomer.mobile}</div>
              <div><strong>Email:</strong> {selectedCustomer.email || "N/A"}</div>
              <div><strong>City:</strong> {selectedCustomer.city || "N/A"}</div>
              <div><strong>Designation:</strong> {selectedCustomer.designation || "N/A"}</div>
            </div>
          )}
        </div>

        <div style={{ height: "1px", background: "#E2E8F0", width: "100%", marginBottom: "25px" }} />

        {/* Row 2 */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px", marginBottom: "25px" }}>
          <div><label style={labelStyleCommon}>Source</label><FormInput isSelect options={SOURCES} value={formState.source} onChange={(v) => setFormState({...formState, source: v})} placeholder="Source" /></div>
          <div><label style={labelStyleCommon}>Property type</label><FormInput isSelect options={PROPERTY_TYPES} value={formState.propertyType} onChange={(v) => setFormState({...formState, propertyType: v})} placeholder="Property Type" /></div>
          <div><label style={labelStyleCommon}>Budget range</label><FormInput isSelect options={BUDGETS} value={formState.budget} onChange={(v) => setFormState({...formState, budget: v})} placeholder="Budget" /></div>
          <div><label style={labelStyleCommon}>Primary goal</label><FormInput isSelect options={GOALS} value={formState.goal} onChange={(v) => setFormState({...formState, goal: v})} placeholder="Goal" /></div>
          <div><label style={labelStyleCommon}>Timeline</label><FormInput isSelect options={TIMELINES} value={formState.timeline} onChange={(v) => setFormState({...formState, timeline: v})} placeholder="Timeline" /></div>
        </div>

        <div style={{ marginBottom: "25px" }}>
          <label style={labelStyleCommon}>Specific location / Area of Interest</label>
          <MultiSelectDropdown 
            options={LOCATIONS} 
            value={formState.remarks} 
            onChange={(v) => setFormState({...formState, remarks: v})} 
            placeholder="Locations" 
          />
        </div>

        {/* Tracking Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px" }}>
          <div>
            <label style={labelStyleCommon}>Next Communication</label>
            <FormInput isSelect options={COMMUNICATION_MODES} value={formState.nextCommunication} onChange={(v) => setFormState({...formState, nextCommunication: v})} placeholder="Comm. Mode" />
          </div>
          <div>
            <label style={labelStyleCommon}>Follow-up person</label>
            <FormInput isSelect options={USERS} value={formState.followUpPerson} onChange={(v) => setFormState({...formState, followUpPerson: v})} placeholder="Follow-up Person" />
          </div>
          <div>
            <label style={labelStyleCommon}>Call Lead (Form Filled By)</label>
            <FormInput isSelect options={LEAD_FROM} value={formState.leadFrom} onChange={(v) => setFormState({...formState, leadFrom: v})} placeholder="Lead Creator" />
          </div>
          <div>
            <label style={labelStyleCommon}>Lead Status</label>
            <FormInput isSelect options={LEAD_STATUS} value={formState.leadStatus} onChange={(v) => setFormState({...formState, leadStatus: v})} placeholder="Status" />
          </div>
        </div>

        {/* Final Actions */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "12px" }}>
          <button 
            onClick={() => navigate('/dashboard/inquiry-details')}
            style={{ background: "white", color: BR, border: `2px solid ${BR}`, padding: "10px 25px", borderRadius: "50px", fontWeight: "700", cursor: "pointer", fontSize: "14px", flex: "1 1 auto", maxWidth: "150px" }}
          >
            ⓘ Details
          </button>
          <button style={{ background: BR, color: "white", border: "none", padding: "10px 40px", borderRadius: "50px", fontWeight: "700", cursor: "pointer", fontSize: "14px", flex: "1 1 auto", maxWidth: "200px" }}>
            💾 Save Lead
          </button>
        </div>
      </div>

      {showModal && <AddCustomerModal onSave={handleCustomerSave} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default LeadEnquiry;